/**
 * useBreadcrumbs — Phase 2.1.4
 * Derives Cloudscape BreadcrumbGroup items from the current route.
 *
 * Route → breadcrumb mapping:
 *   /knowledge/browse              → [Home, Browse Articles]
 *   /knowledge/articles/:id        → [Home, Browse Articles, {article title}]
 *   /knowledge/create              → [Home, Create Article]
 *   /knowledge/articles/:id/edit   → [Home, Browse Articles, {article title}, Edit]
 *   /knowledge/validation          → [Home, Validation Queue]
 *   /knowledge/dashboard           → [Home, My Dashboard]
 *   /knowledge/milestones          → [Home, Launch Milestones]
 *   /knowledge/gaps                → [Home, Knowledge Gaps]
 *   /admin                         → [Home, Admin]
 *   /admin/*                       → [Home, Admin, ...]
 */

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { BreadcrumbGroupProps } from '@cloudscape-design/components/breadcrumb-group';

const HOME: BreadcrumbGroupProps.Item = { text: 'Home', href: '/knowledge/browse' };

/**
 * Returns breadcrumb items for the current route.
 * Pass `articleTitle` when on an article view/edit page so the title
 * can be shown instead of the raw ID.
 */
export function useBreadcrumbs(articleTitle?: string): BreadcrumbGroupProps.Item[] {
  const location = useLocation();

  return useMemo(() => {
    const path = location.pathname;
    // Extract articleId from the URL path directly. useParams() cannot be used
    // here because KnowledgeLayout renders above the nested <Routes> that defines
    // :articleId — React Router v6 only exposes params from the nearest ancestor
    // <Route>. TODO: Refactor to <Outlet>-based layout routes so useParams works
    // natively and this regex can be removed.
    const articleIdFromPath = path.match(/\/knowledge\/articles\/([^/]+)/)?.[1];

    // /knowledge/articles/:id/edit
    if (/^\/knowledge\/articles\/[^/]+\/edit/.test(path)) {
      return [
        HOME,
        { text: 'Browse Articles', href: '/knowledge/browse' },
        {
          text: articleTitle ?? 'Article',
          href: `/knowledge/articles/${articleIdFromPath}`,
        },
        { text: 'Edit', href: path },
      ];
    }

    // /knowledge/articles/:id
    if (/^\/knowledge\/articles\/[^/]+/.test(path)) {
      return [
        HOME,
        { text: 'Browse Articles', href: '/knowledge/browse' },
        { text: articleTitle ?? 'Article', href: path },
      ];
    }

    // Static knowledge routes
    if (path.startsWith('/knowledge/browse')) {
      return [HOME, { text: 'Browse Articles', href: '/knowledge/browse' }];
    }
    if (path.startsWith('/knowledge/create')) {
      return [HOME, { text: 'Create Article', href: '/knowledge/create' }];
    }
    if (path.startsWith('/knowledge/validation')) {
      return [HOME, { text: 'Validation Queue', href: '/knowledge/validation' }];
    }
    if (path.startsWith('/knowledge/dashboard')) {
      return [HOME, { text: 'My Dashboard', href: '/knowledge/dashboard' }];
    }
    if (path.startsWith('/knowledge/milestones')) {
      return [HOME, { text: 'Launch Milestones', href: '/knowledge/milestones' }];
    }
    if (path.startsWith('/knowledge/gaps')) {
      return [HOME, { text: 'Knowledge Gaps', href: '/knowledge/gaps' }];
    }

    // /admin/* — split remaining segments into crumbs
    if (path.startsWith('/admin')) {
      const adminBase: BreadcrumbGroupProps.Item = { text: 'Admin', href: '/admin' };
      const segments = path.replace(/^\/admin\/?/, '').split('/').filter(Boolean);
      if (segments.length === 0) {
        return [HOME, adminBase];
      }
      const extra: BreadcrumbGroupProps.Item[] = segments.map((seg, i) => {
        const href = '/admin/' + segments.slice(0, i + 1).join('/');
        // Convert kebab-case to Title Case for display
        const text = seg
          .split('-')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
        return { text, href };
      });
      return [HOME, adminBase, ...extra];
    }

    // Fallback — just Home
    return [HOME];
  }, [location.pathname, articleTitle]);
}
